import { Col, Form, Row } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import queryKeys from "common/queryKey";
import { useScrollFetch } from "hooks/useScrollFetch";
import { uniqBy } from "lodash";
import React, { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilter } from "redux/actions";
import { getClientid, getFilters, getUserList } from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { utils, writeFileXLSX } from "xlsx";
import BackLogRender from "./BackLogRender";
import styled from "styled-components";

function TimesheetReport() {
  const clientId = useSelector(getClientid);
  const userList = useSelector(getUserList);
  const filters = useSelector(getFilters);

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const menuName = "timesheetReport";
  const initialFilter = filters[menuName];

  const { getAllTimesheetReport } = queryKeys;
  const { key, url } = getAllTimesheetReport();

  const { data, lastElementRef, isLoading } = useScrollFetch(key, {
    endPoint: url,
    queryParam: null,
  });

  const columns = useMemo(() => [
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "employeeId",
      key: "employeeId",
      width: 250,
    },
    {
      title: "Pending",
      dataIndex: "pending",
      key: "pending",
      width: 100,
    },
    {
      title: "Approved",
      dataIndex: "approved",
      key: "approved",
      width: 100,
    },
    {
      title: "Backlog",
      dataIndex: "backlog",
      key: "backlog",
      width: 100,
      render: (text, record) => {
        return <BackLogRender record={record} />;
      },
    }

  ], []);

  const dataList = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages.flatMap(page => page?.response || []);
  }, [data]);

  const reportMap = useMemo(() => {
    const map = new Map();
    dataList.forEach(item => {
      if (item?.email) {
        map.set(item.email, item);
      }
    });
    return map;
  }, [dataList]);

  const activeUsers = useMemo(() => {
    return userList?.filter(user => user?.statusid === "1") || [];
  }, [userList]);

  const allDataList = useMemo(() => {
    return activeUsers.map(user => {
      const report = reportMap.get(user?.userid);
      if (report) {
        return {
          employeeId: user.userid,
          employeeName: user.fullname,
          email: report.email,
          pending: Number(report.pending) || 0,
          approved: Number(report.approved) || 0,
          draft: Number(report.draft) || 0,
          backlog: Number(report.draft) || 0,
        };
      }

      return {
        employeeId: user.userid,
        employeeName: user.fullname,
        email: user.userid,
        pending: 0,
        approved: 0,
        draft: 0,
        backlog: 0,
      };
    });
  }, [activeUsers, reportMap]);

  const userOptions = useMemo(() => {
    return allDataList.map(user => ({
      label: user.employeeName,
      value: user.employeeId,
    }));
  }, [allDataList]);

  const filteredData = useMemo(() => {
    const selectedNames = form.getFieldValue("name") || [];
    if (selectedNames.length === 0) {
      return allDataList;
    }
    const nameSet = new Set(selectedNames);
    return allDataList.filter(item => nameSet.has(item.employeeId));
  }, [allDataList, filters?.[menuName]?.name]);

  const handleFormChange = useCallback(() => {
    const values = form.getFieldsValue();
    dispatch(setFilter(menuName, "name", values.name || []));
  }, [form, dispatch, menuName]);

  const xport = useCallback(() => {
    const elt = document.getElementById("Table");
    const wb = utils.table_to_book(elt);
    writeFileXLSX(wb, "Timesheet Report.csv");
  }, []);

  return (
    <TimeSheetReportWrap>
      <Row>
        <Col sm={24}>
          <div className="d-flex justify-content-between">
            <Title>Timesheet Report</Title>
            <ButtonOutlined className="text-end" onClick={xport} type="link">
              Export CSV file
            </ButtonOutlined>
          </div>
        </Col>
        <Col sm={24}>
          <Form
            form={form}
            layout="vertical"
            className="mt-2"
            onValuesChange={handleFormChange}
            initialValues={{ ...initialFilter }}
          >
            <Row gutter={12} className="g-3 mb-2">
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="name"
                  label="Name"
                  options={uniqBy(userOptions, "value")}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      <Table
        className="pt-0"
        dataSource={filteredData}
        columns={columns}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} employees`,
        }}
        loading={isLoading}
        rowKey="employeeId"
        onRow={(record, index) => ({
          ref: index === filteredData.length - 20 ? lastElementRef : null,
        })}
        id="Table"
        scroll={{ x: 800 }}
      />
    </TimeSheetReportWrap>
  );
}

export default React.memo(TimesheetReport);

const TimeSheetReportWrap = styled.div`
  .backlog {
    cursor: pointer;
    color: #1890ff;
  }
`;